import React from 'react'
import loadable from '@loadable/component'
import { useIntl } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useOrganization } from 'contexts/organization'
import { useWhistleblowing } from 'contexts/whistleblowing'
import paths from 'app/paths'
import { compact } from 'utilities/array'
import Hero from './hero'
import { Tutorial } from './s'
import { Routes, Route } from 'react-router-dom'
import { TabsAndPanes, getPathWithoutBase } from 'components/tabs'
import RouteAuthorization from 'app/route-authorization'
import DelayedRedirect from 'components/delayed-redirect'

import UnresolvedPane from './panes/unresolved'
const ArchivePane = loadable(() => import(/* webpackPrefetch: true */ './panes/archive'))
const SettingsPane = loadable(() => import(/* webpackPrefetch: true */ './panes/settings'))

const CasePage = loadable(() => import(/* webpackPrefetch: true */ './pages/case'))

const salt = 'whistleblowing'

const WhistleblowingRouting = ({ upgradable }) => {
    const { formatMessage } = useIntl()

    const { check } = useAccess()
    const admin = check('organization:manage')
    const handler = check('whistleblowing:manage')

    const { statuses } = useOrganization()

    const {
        configuration,
        settingUp
    } = useWhistleblowing()

    const { base } = paths.whistleblowing
    const getRelativePath = getPathWithoutBase(base)

    const settingsLabel = formatMessage({
        id: 'noun_settings',
        defaultMessage: 'Settings'
    })

    const panes = compact([
        handler && {
            path: base,
            label: formatMessage({
                id: 'whistleblowing_section_unresolved',
                defaultMessage: 'Unresolved cases'
            }),
            pane: UnresolvedPane,
            pepper: 'base'
        },
        handler && {
            path: paths.whistleblowing.archive,
            label: formatMessage({
                id: 'whistleblowing_section_archive',
                defaultMessage: 'Archive'
            }),
            pane: ArchivePane,
            pepper: 'archive'
        },
        (admin || handler) && {
            path: !handler ?
                base :
                paths.whistleblowing.settings,
            label: settingsLabel,
            pane: SettingsPane,
            ...(!handler ? { tabless: true } : null),
            pepper: 'settings'
        }
    ]).map(pane => ({
        ...pane,
        upgradable: pane.upgradable ?? upgradable
    }))

    const unconfigured = (!!statuses && !statuses?.hasSetUpWhistleblowing) || settingUp
    const inactive = !!configuration && !configuration?.active

    return (
        <Routes>
            <Route element={(
                <>
                    <Hero
                        upgradable={upgradable}
                        unconfigured={unconfigured}
                        salt={salt} />
                    {(unconfigured || inactive) && <Tutorial which="whistleblowing" />}
                    {(!unconfigured && !!configuration?.active) && (
                        <TabsAndPanes
                            panes={panes}
                            salt={salt} />
                    )}
                </>
            )}>
                {panes.map(({ path, pane: Pane, label, upgradable, pepper, ...props }) => {
                    const key = `${salt}:pane:${pepper}`

                    return (
                        <Route
                            path={getRelativePath(path)}
                            element={(
                                <RouteAuthorization
                                    module="whistleblowing"
                                    upgradable={upgradable}>
                                    <Pane
                                        {...props}
                                        title={label}
                                        salt={salt}
                                        key={key} />
                                </RouteAuthorization>
                            )}
                            key={`${key}:route`} />
                    )
                })}
            </Route>
            {/* Hack so that there’s always a route mounted for the settings path */}
            {(admin && !handler) && (
                <Route
                    path={getRelativePath(paths.whistleblowing.settings)}
                    element={(
                        <RouteAuthorization module="whistleblowing">
                            <SettingsPane />
                        </RouteAuthorization>
                    )} />
            )}
            <Route
                path={getRelativePath(paths.whistleblowing.case)}
                element={(
                    <RouteAuthorization module="whistleblowing">
                        <CasePage />
                    </RouteAuthorization>
                )} />
            <Route
                path="*"
                element={<DelayedRedirect to={base} />} />
        </Routes>
    )
}

export default WhistleblowingRouting